import React from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectLabels, selectLanguage } from "../../slices/language";
import { CircularProgress, Divider } from "@mui/material";
import api from "../../api";
import { showError } from "../../utils/helpers";
import { AccessRelation } from "../../api/models/access-model";
import ImageModal from "../Modals/ImageModal";
import IconifyIcon from "../IconifyIcon";
import dayjs from "dayjs";
import {
  AccessKeyCreateResponse,
} from "../../api/models/access-key-model";
import {
  getAccessTransactionPhotoUrl,
  toAbsoluteMediaUrl,
} from "../../utils/getAccessTransactionPhotoUrl";

interface DesktopRegisterDetailsProps {
  selectedAccessId: string | null;
  isMobile: boolean;
}

const DesktopRegisterDetails = ({
  selectedAccessId,
  isMobile,
}: DesktopRegisterDetailsProps) => {
  const labels = useAppSelector(selectLabels);
  const dispatch = useAppDispatch();
  const language = useAppSelector(selectLanguage);

  const currentLanguage = language.startsWith("es") ? "es" : "en";

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [currentAccess, setCurrentAccess] =
    React.useState<AccessRelation | null>(null);
  const [selectedPhoto, setSelectedPhoto] = React.useState<{
    type: string;
    url: string;
  } | null>(null);
  const [showPictureModal, setShowPictureModal] =
    React.useState<boolean>(false);
  const [currentAccessKey, setCurrentAccessKey] =
    React.useState<AccessKeyCreateResponse | null>(null);

  const requirements =
    currentAccess?.entrance_transaction?.requirements ?? [];

  const accessKeyId =
    currentAccess?.entrance_transaction?.access_key_id ||
    currentAccess?.exit_transaction?.access_key_id;

  const photos: { type: string; url: string }[] = React.useMemo(() => {
    return (
      currentAccess?.entrance_transaction?.access_transaction_photos?.map(
        (item) => {
          return {
            type: item?.type, //photo id | plates | visitor | equipment
            url: getAccessTransactionPhotoUrl(item),
          };
        },
      ) || []
    );
  }, [
    currentAccess?.entrance_transaction?.access_transaction_photos,
  ]);

  const message =
    currentAccess?.entrance_transaction !== null
      ? currentAccess?.entrance_transaction?.status?.message[currentLanguage]
      : currentAccess?.exit_transaction?.status?.message[currentLanguage];

  React.useEffect(() => {
    if (photos && photos.length > 0) {
      const photoId =
        photos.filter((item) => item?.type === "photo id")[0] || null;
      const photoPlates =
        photos.filter((item) => item?.type === "plates")[0] || null;
      const photoVisitor =
        photos.filter((item) => item?.type === "visitor")[0] || null;
      const photoEquipment =
        photos.filter((item) => item?.type === "equipment")[0] || null;

      if (photoId) {
        setSelectedPhoto(photoId);
      } else if (photoPlates) {
        setSelectedPhoto(photoPlates);
      } else if (photoVisitor) {
        setSelectedPhoto(photoVisitor);
      } else if (photoEquipment) {
        setSelectedPhoto(photoEquipment);
      }
    }
  }, [photos]);

  const getAccessData = React.useCallback(async () => {
    if (selectedAccessId) {
      try {
        setIsLoading(true);
        const response = await api.getAccessesByIdRequest(selectedAccessId);
        if (response) {
          console.log("logbook detailsresponse", response);
          setCurrentAccess(response);
        }
      } catch (error: any) {
        showError(dispatch, labels.generic_error);
      } finally {
        setIsLoading(false);
      }
    }
  }, [dispatch, labels.generic_error, selectedAccessId]);

  React.useEffect(() => {
    getAccessData();
  }, [getAccessData]);

  const getAccessKeyData = React.useCallback(async () => {
    if (accessKeyId) {
      try {
        const response = await api.singleAccessKeyByIdRequest(
          Number(accessKeyId),
        );
        if (response) {
          setCurrentAccessKey(response);
        }
      } catch (error: any) {
        showError(dispatch, labels.generic_error);
      }
    }
  }, [accessKeyId, dispatch, labels.generic_error]);

  React.useEffect(() => {
    getAccessKeyData();
  }, [getAccessKeyData]);

  const imageToShow = React.useMemo(
    () => toAbsoluteMediaUrl(selectedPhoto?.url),
    [selectedPhoto],
  );

  return (
    <div className="w-100 border-box hidden">
      <ImageModal
        showModal={showPictureModal}
        setShowModal={setShowPictureModal}
        // srcImage={`${selectedPhoto?.url}`}
        srcImage={imageToShow}
        imageType={`${selectedPhoto?.type}`}
        visitorName={`${currentAccess?.visitor}`}
      />
      {isLoading ? (
        <div className="w-100 flex align-items-center justify-content-center mt-12">
          <CircularProgress />
        </div>
      ) : (
        <div className="w-100 border-box flex flex-dir-col gap-4">
          <div
            className="w-100 border-box flex flex-dir-col align-items-center gap-6 p-8"
            style={{
              backgroundColor: "white",
              border: "1px #e0e0e0 solid",
              borderRadius: 6,
            }}
          >
            {!currentAccess?.authorized && (
              <div className="w-100 flex flex-dir-col align-items-center justify-content-center">
                <div className="w-100 flex align-items-center justify-content-center gap-4 mb-4">
                  <IconifyIcon
                    icon={"entypo:block"}
                    style={{ fontSize: 20 }}
                    className="red-color"
                  />
                  <p
                    className={`text-center font-18 font-w-700 red-color uppercase`}
                  >
                    {labels.not_authorized}
                  </p>
                </div>
                <p className={`text-center font-14 font-w-500 red-color`}>
                  {message}
                </p>
              </div>
            )}
            <div className="w-100 border-box">
              {currentAccess?.visitor && (
                <p className="body-text-xl font-w-500 mb-10">
                  {currentAccess?.visitor}
                </p>
              )}
              <p className="body-text-md mt-2 mb-4">
                {labels.user}: {currentAccess?.user}
              </p>
              <p className="body-text-md mt-2 mb-4">
                {labels.unit}: {currentAccess?.unit}
              </p>
              <Divider />
            </div>
            {currentAccessKey?.identifier && (
              <div className="w-100 border-box align-items-center">
                <p className="body-text-md mt-2">
                  {labels.identifier}: {currentAccessKey?.identifier}
                </p>
              </div>
            )}
            {currentAccessKey?.reason_for_visit && (
              <div className="w-100 border-box align-items-center">
                <p className="body-text-md mt-2">
                  {currentAccessKey?.reason_for_visit}
                </p>
              </div>
            )}
            <div className="w-100 border-box">
              {photos && photos.length > 0 && selectedPhoto && (
                <div
                  className="flex align-items-center"
                  style={{
                    width: "100%",
                    height: 200,
                    backgroundColor: "black",
                    borderTopRightRadius: 4,
                    borderTopLeftRadius: 4,
                    marginRight: 8,
                    overflow: "hidden",
                    cursor: "pointer",
                  }}
                  onClick={() => setShowPictureModal(true)}
                >
                  <img
                    // src={selectedPhoto?.url}
                    src={imageToShow}
                    alt=""
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </div>
              )}
              {photos && photos.length > 0 && (
                <div
                  className="w-100 border-box px-4 py-2 flex align-items-center justify-content-end gap-2"
                  style={{
                    backgroundColor: "#f0f0f0",
                    borderBottomLeftRadius: 4,
                    borderBottomRightRadius: 4,
                    border: "1px #e0e0e0 solid",
                  }}
                >
                  {photos.map((item, index) => (
                    <div
                      key={`${item?.type}-${index}`}
                      className={
                        selectedPhoto?.type === item?.type
                          ? "photo-button-preview-logbook-selected"
                          : "photo-button-preview-logbook"
                      }
                      style={{ cursor: "pointer" }}
                      onClick={() => setSelectedPhoto(item)}
                    >
                      <IconifyIcon
                        icon={
                          item?.type === "photo id"
                            ? "fa-regular:id-card"
                            : item?.type === "plates"
                              ? "fa-solid:car-alt"
                              : item?.type === "visitor"
                                ? "fa-solid:user"
                                : "fa-solid:laptop"
                        }
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
            {currentAccess?.entrance_transaction !==
              null && (
              <div
                className="w-100 border-box"
                style={{ border: "1px #e0e0e0 solid" }}
              >
                <div className="w-100 border-box flex align-items-center p-4">
                  <div
                    className={
                      isMobile
                        ? "w-5 flex justify-content-start"
                        : "w-15 flex justify-content-start"
                    }
                  >
                    {isMobile ? (
                      <IconifyIcon
                        icon={"entypo:login"}
                        className="subtitle-color"
                      />
                    ) : (
                      <p className="body-text-sm">{labels.entrance}</p>
                    )}
                  </div>
                  <div
                    className={
                      isMobile
                        ? "w-40 flex justify-content-center"
                        : "w-30 flex justify-content-center"
                    }
                  >
                    <p className="body-text-sm">
                      {isMobile ? (
                        <p className="body-text-sm">
                          {dayjs(
                            currentAccess
                              ?.entrance_transaction?.datetime,
                          ).format("DD/MM hh:mm")}
                        </p>
                      ) : (
                        <p className="body-text-sm">
                          {dayjs(
                            currentAccess
                              ?.entrance_transaction?.datetime,
                          ).format("DD/MMMM hh:mm")}
                        </p>
                      )}
                    </p>
                  </div>
                  <div className="w-55 flex justify-content-end">
                    <p className="body-text-sm">
                      {
                        currentAccess?.entrance_transaction
                          ?.gate?.access_point?.name
                      }
                    </p>
                  </div>
                </div>
                {currentAccess &&
                  currentAccess?.entrance_transaction
                    ?.users_permissions_users &&
                  currentAccess?.entrance_transaction
                    ?.users_permissions_users?.length > 0 && (
                    <div
                      className="w-100 border-box"
                      style={{ backgroundColor: "#f0f0f0" }}
                    >
                      <div className="w-100 border-box flex flex-dir-col align-items-center gap-6 px-4 py-2">
                        {currentAccess?.entrance_transaction?.users_permissions_users?.map(
                          (user, index) => (
                            <div className="w-100" key={`${user?.id}-${index}`}>
                              <p className="body-text-xs">
                                {labels.entrance_registered_by}:{" "}
                                <span className="font-w-500">
                                  {user?.name ||
                                    user?.username}
                                </span>
                              </p>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  )}
              </div>
            )}
            {currentAccess?.exit_transaction !== null &&
              currentAccess?.exit_transaction
                ?.datetime && (
                <div
                  className="w-100 border-box"
                  style={{ border: "1px #e0e0e0 solid" }}
                >
                  <div className="w-100 border-box flex align-items-center p-4">
                    <div
                      className={
                        isMobile
                          ? "w-5 flex justify-content-start"
                          : "w-15 flex justify-content-start"
                      }
                    >
                      {isMobile ? (
                        <IconifyIcon
                          icon={"entypo:log-out"}
                          className="subtitle-color"
                        />
                      ) : (
                        <p className="body-text-sm">{labels.exit}</p>
                      )}
                    </div>
                    <div
                      className={
                        isMobile
                          ? "w-40 flex justify-content-center"
                          : "w-30 flex justify-content-center"
                      }
                    >
                      {isMobile ? (
                        <p className="body-text-sm">
                          {dayjs(
                            currentAccess?.exit_transaction
                              ?.datetime,
                          ).format("DD/MM hh:mm")}
                        </p>
                      ) : (
                        <p className="body-text-sm">
                          {dayjs(
                            currentAccess?.exit_transaction
                              ?.datetime,
                          ).format("DD/MMMM hh:mm")}
                        </p>
                      )}
                    </div>
                    <div className="w-55 flex justify-content-end">
                      <p className="body-text-sm">
                        {
                          currentAccess?.exit_transaction
                            ?.gate?.access_point?.name
                        }
                      </p>
                    </div>
                  </div>
                  {currentAccess &&
                    currentAccess?.exit_transaction
                      ?.users_permissions_users &&
                    currentAccess?.exit_transaction
                      ?.users_permissions_users?.length >
                      0 && (
                      <div
                        className="w-100 border-box"
                        style={{ backgroundColor: "#f0f0f0" }}
                      >
                        <div className="w-100 border-box flex flex-dir-col align-items-center gap-6 px-4 py-2">
                          {currentAccess?.exit_transaction?.users_permissions_users?.map(
                            (user, index) => (
                              <div
                                className="w-100"
                                key={`${user?.id}-${index}`}
                              >
                                <p className="body-text-xs">
                                  {labels.exit_registered_by}:{" "}
                                  <span className="font-w-500">
                                    {user?.name ||
                                      user?.username}
                                  </span>
                                </p>
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    )}
                </div>
              )}
            {requirements.length > 0 && (
              <div className="w-100 border-box flex flex-dir-col gap-4">
                <Divider />
                <div className="w-100 border-box flex flex-dir-col align-items-center gap-6 px-4">
                  {requirements.map((requirement, index) => (
                    <div className="w-100" key={`${requirement?.id}-${index}`}>
                      <p className="body-text-md">
                        {
                          requirement?.requirement_type?.name
                        }
                        : {requirement?.value}
                      </p>
                    </div>
                  ))}
                </div>
                <Divider />
              </div>
            )}
            <div className="w-100 flex justify-content-start">
              <p className="body-text-sm">
                {labels.type_of_registration}:{" "}
                <span className="font-w-500">
                  {currentAccess?.entrance_transaction
                    ?.access_key_id ||
                  currentAccess?.exit_transaction
                    ?.access_key_id
                    ? labels.invitation
                    : labels.register}
                </span>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DesktopRegisterDetails;
